import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Rating,
  Chip,
  Stack,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import api from '../config/api';

interface Movie {
  _id?: string;
  title: string;
  year: string;
  imdbId: string;
  poster: string;
  plot?: string;
  imdbRating?: string;
  metascore?: string;
  director?: string;
  actors?: string;
  runtime?: string;
  genre?: string;
}

interface OMDBMovie {
  Title: string;
  Year: string;
  imdbID: string;
  Poster: string;
  Plot?: string;
  imdbRating?: string;
  Metascore?: string;
  Director?: string;
  Actors?: string;
  Runtime?: string;
  Genre?: string;
}

interface Watchlist {
  _id: string;
  name: string;
  description: string;
  createdBy: string;
  movies: Movie[];
  isActive: boolean;
}

const WatchlistDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [watchlist, setWatchlist] = useState<Watchlist | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<OMDBMovie[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchWatchlist = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await api.get(`/api/watchlists/${id}`);
        setWatchlist(response.data);
      } catch (error) {
        console.error('Error fetching watchlist:', error);
        setError('Failed to fetch watchlist');
      } finally {
        setLoading(false);
      }
    };

    fetchWatchlist();
  }, [id]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/api/movies/search?query=${searchQuery}`);
      const movies = response.data;

      // Fetch full details for each movie
      const moviesWithDetails = await Promise.all(
        movies.map(async (movie: OMDBMovie) => {
          const detailsResponse = await api.get(`/api/movies/${movie.imdbID}`);
          return detailsResponse.data;
        })
      );

      setSearchResults(moviesWithDetails);
    } catch (error) {
      console.error('Error searching movies:', error);
      setError('Failed to search movies');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMovie = async (movie: OMDBMovie) => {
    try {
      setLoading(true);
      setError('');
      const payload = {
        imdbID: movie.imdbID,
        Title: movie.Title,
        Year: movie.Year,
        Poster: movie.Poster,
        imdbRating: movie.imdbRating,
        Metascore: movie.Metascore,
        Genre: movie.Genre,
        Director: movie.Director,
        Actors: movie.Actors,
        Plot: movie.Plot,
        Runtime: movie.Runtime
      };

      await api.post(`/api/watchlists/${id}/movies`, payload);
      
      // Refresh watchlist
      const watchlistResponse = await api.get(`/api/watchlists/${id}`);
      setWatchlist(watchlistResponse.data);
    } catch (error) {
      console.error('Error adding movie:', error);
      setError('Failed to add movie to watchlist');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMovie = async (movieId: string) => {
    try {
      setLoading(true);
      setError('');
      await api.delete(`/api/watchlists/${id}/movies/${movieId}`);
      
      // Refresh watchlist
      const watchlistResponse = await api.get(`/api/watchlists/${id}`);
      setWatchlist(watchlistResponse.data);
    } catch (error) {
      console.error('Error deleting movie:', error);
      setError('Failed to delete movie from watchlist');
    } finally {
      setLoading(false);
    }
  };

  if (!watchlist) {
    return <Typography>Loading...</Typography>;
  }

  const MovieListItem = ({ movie, isSearchResult = false }: { movie: Movie | OMDBMovie, isSearchResult?: boolean }) => {
    const normalizedMovie = {
      title: 'title' in movie ? movie.title : movie.Title,
      year: 'year' in movie ? movie.year : movie.Year,
      imdbId: 'imdbId' in movie ? movie.imdbId : movie.imdbID,
      poster: 'poster' in movie ? movie.poster : movie.Poster,
      plot: 'plot' in movie ? movie.plot : ('Plot' in movie ? (movie as OMDBMovie).Plot : undefined),
      imdbRating: 'imdbRating' in movie ? movie.imdbRating : ('imdbRating' in movie ? (movie as OMDBMovie).imdbRating : undefined),
      metascore: 'metascore' in movie ? movie.metascore : ('Metascore' in movie ? (movie as OMDBMovie).Metascore : undefined),
      director: 'director' in movie ? movie.director : ('Director' in movie ? (movie as OMDBMovie).Director : undefined),
      actors: 'actors' in movie ? movie.actors : ('Actors' in movie ? (movie as OMDBMovie).Actors : undefined),
      runtime: 'runtime' in movie ? movie.runtime : ('Runtime' in movie ? (movie as OMDBMovie).Runtime : undefined),
      genre: 'genre' in movie ? movie.genre : ('Genre' in movie ? (movie as OMDBMovie).Genre : undefined),
    };

    return (
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={2} sm={1}>
              <Box
                component="img"
                src={normalizedMovie.poster !== 'N/A' ? normalizedMovie.poster : 'https://via.placeholder.com/50x75'}
                alt={normalizedMovie.title}
                sx={{
                  width: '50px',
                  height: '75px',
                  objectFit: 'cover',
                }}
              />
            </Grid>
            <Grid item xs={7} sm={8}>
              <Typography variant="subtitle1">
                {normalizedMovie.title} ({normalizedMovie.year})
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                {normalizedMovie.imdbRating && (
                  <>
                    <Rating
                      value={parseFloat(normalizedMovie.imdbRating) / 2}
                      precision={0.5}
                      readOnly
                      size="small"
                    />
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      {normalizedMovie.imdbRating}
                    </Typography>
                  </>
                )}
                {normalizedMovie.metascore && (
                  <>
                    <Typography variant="body2" sx={{ ml: 2 }}>
                      Metascore:
                    </Typography>
                    <Chip
                      label={normalizedMovie.metascore}
                      color={parseInt(normalizedMovie.metascore) >= 60 ? 'success' : 'warning'}
                      size="small"
                    />
                  </>
                )}
                {normalizedMovie.runtime && (
                  <>
                    <Typography variant="body2" sx={{ ml: 2 }}>
                      {normalizedMovie.runtime}
                    </Typography>
                  </>
                )}
                {normalizedMovie.genre && (
                  <>
                    <Typography variant="body2" sx={{ ml: 2 }}>
                      {normalizedMovie.genre.split(', ')[0]}
                    </Typography>
                  </>
                )}
              </Stack>
            </Grid>
            <Grid item xs={3} sm={3} sx={{ textAlign: 'right' }}>
              {isSearchResult ? (
                <IconButton
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddMovie(movie as OMDBMovie);
                  }}
                  disabled={loading}
                >
                  <AddIcon />
                </IconButton>
              ) : (
                <IconButton
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteMovie((movie as Movie)._id!);
                  }}
                  disabled={loading}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Grid>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            {normalizedMovie.genre && (
              <Grid item xs={12}>
                <Stack direction="row" spacing={1}>
                  {normalizedMovie.genre.split(', ').map((genre: string) => (
                    <Chip key={genre} label={genre} size="small" />
                  ))}
                </Stack>
              </Grid>
            )}
            {normalizedMovie.director && (
              <Grid item xs={12}>
                <Typography variant="body2">
                  <strong>Director:</strong> {normalizedMovie.director}
                </Typography>
              </Grid>
            )}
            {normalizedMovie.actors && (
              <Grid item xs={12}>
                <Typography variant="body2">
                  <strong>Actors:</strong> {normalizedMovie.actors}
                </Typography>
              </Grid>
            )}
            {normalizedMovie.plot && (
              <Grid item xs={12}>
                <Typography variant="body2">
                  <strong>Plot:</strong> {normalizedMovie.plot}
                </Typography>
              </Grid>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {watchlist.name}
      </Typography>
      <Typography color="text.secondary" paragraph>
        {watchlist.description}
      </Typography>

      <Box sx={{ mb: 4 }}>
        <TextField
          fullWidth
          label="Search for movies"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mb: 2 }}
          error={!!error}
          helperText={error}
        />
        <Button 
          variant="contained" 
          onClick={handleSearch}
          disabled={loading}
        >
          {loading ? 'Searching...' : 'Search'}
        </Button>
      </Box>

      {searchResults.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Search Results
          </Typography>
          <Stack spacing={1}>
            {searchResults.map((movie) => (
              <MovieListItem key={movie.imdbID} movie={movie} isSearchResult />
            ))}
          </Stack>
        </Box>
      )}

      <Typography variant="h6" gutterBottom>
        Movies in Watchlist
      </Typography>
      <Stack spacing={1}>
        {watchlist.movies.map((movie) => (
          <MovieListItem key={movie._id} movie={movie} />
        ))}
      </Stack>
    </Container>
  );
};

export default WatchlistDetail; 